import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MainLayout from "./layouts/MainLayout";
import Page404 from "./pages/errors/Page404";
import Dashboard from "./pages/dashboard";
import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import Main from "./pages/manage/Main";
import AuthLayout from "./layouts/AuthLayout";
import Signin from "./pages/auth/Signin";
import Forgot from "./pages/auth/Forgot";
import Reset from "./pages/auth/Reset";
import Denied from "./pages/auth/Denied";
import AllTrainings from "./pages/trainings/AllTrainings";
import OneTraining from "./pages/trainings/OneTraining";
import AllCourses from "./pages/courses/AllCourses";
import OneCourse from "./pages/courses/OneCourse";
import AllMembers from "./pages/members/AllMembers";
import OneMember from "./pages/members/OneMember";
import AllUpcoming from "./pages/trainings/AllUpcoming";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import MyProfile from "./pages/profile/MyProfile";
import AllSettings from "./pages/settings/AllSettings";
import AllNotifications from "./pages/notifications/AllNotifications";
import AllMessages from "./pages/messages/AllMessages";
import Signup from "./pages/auth/Signup";

export default function App() {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="/" element={<Signin />} />
            <Route path="/register" element={<Signup />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/denied" element={<Denied />} />
          </Route>
          <Route element={<MainLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/upcoming"
              element={
                <ProtectedRoute>
                  <AllUpcoming />
                </ProtectedRoute>
              }
            />

            <Route
              path="/upcoming/:id"
              element={
                <ProtectedRoute>
                  <OneTraining />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainings"
              element={
                <ProtectedRoute>
                  <AllTrainings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainings/:id"
              element={
                <ProtectedRoute>
                  <OneTraining />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses"
              element={
                <ProtectedRoute>
                  <AllCourses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id"
              element={
                <ProtectedRoute>
                  <OneCourse />
                </ProtectedRoute>
              }
            />

            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <AllMembers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/members/:id"
              element={
                <ProtectedRoute>
                  <OneMember />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AllSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <AllMessages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <AllNotifications />
                </ProtectedRoute>
              }
            />

            <Route path="/manage" element={<Main />} />
          </Route>
          <Route path="*" element={<Page404 />} />
        </Routes>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  let { token } = React.useContext(AuthContext);
  let location = useLocation();

  if (!token) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}
